import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import leaveService from "./leaveService";

const getUserFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const LeaveRequest = () => {
  const toast = useToast();
  const user = getUserFromStorage();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [requests, setRequests] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [selectedPolicyForBalance, setSelectedPolicyForBalance] = useState("");
  const [balance, setBalance] = useState(null);
  const [allBalances, setAllBalances] = useState([]);

  const [form, setForm] = useState({
    userId: user?._id || "",
    leavePolicyId: "",
    fromDate: "",
    toDate: "",
    reason: "",
    isHalfDay: false,
    halfDaySlot: "FIRST_HALF",
  });

  const [filters, setFilters] = useState({ status: "" });

  const loadRequests = async () => {
    try {
      const data = await leaveService.getLeaveRequests({
        userId: user?._id,
        ...filters,
      });
      setRequests(data || []);
    } catch (error) {
      toast({
        title: "Failed to load leave requests",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const loadPolicies = async () => {
    try {
      const data = await leaveService.getPolicies({ isActive: true });
      setPolicies(data || []);
    } catch (error) {
      toast({
        title: "Failed to load policies",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const loadBalance = async (policyId) => {
    if (!policyId || !user?._id) return;
    try {
      const data = await leaveService.getLeaveBalance({
        userId: user._id,
        leavePolicyId: policyId,
      });
      setBalance(data);
    } catch (error) {
      setBalance(null);
      toast({
        title: "Leave balance not found",
        description: "Ask HR to initialize your leave balance for this policy.",
        status: "info",
      });
    }
  };

  const loadAllBalances = async () => {
    if (!user?._id || policies.length === 0) return;
    try {
      const balances = await Promise.all(
        policies.map((policy) =>
          leaveService
            .getLeaveBalance({
              userId: user._id,
              leavePolicyId: policy._id,
            })
            .catch(() => null),
        ),
      );
      setAllBalances(balances.filter((b) => b !== null));
    } catch (error) {
      console.error("Error loading balances:", error);
    }
  };

  useEffect(() => {
    loadPolicies();
    loadRequests();
  }, []);

  useEffect(() => {
    if (policies.length > 0) {
      loadAllBalances();
    }
  }, [policies]);

  useEffect(() => {
    loadRequests();
  }, [filters.status]);

  const submitRequest = async () => {
    if (!form.leavePolicyId || !form.fromDate || !form.toDate || !form.reason) {
      toast({ title: "Please fill all required fields", status: "warning" });
      return;
    }

    try {
      await leaveService.createLeaveRequest({
        ...form,
        userId: user?._id,
      });

      toast({ title: "Leave request created", status: "success" });
      setForm((prev) => ({
        ...prev,
        leavePolicyId: "",
        fromDate: "",
        toDate: "",
        reason: "",
        isHalfDay: false,
        halfDaySlot: "FIRST_HALF",
      }));
      onClose();
      loadRequests();
    } catch (error) {
      toast({
        title: "Failed to create leave request",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const cancelRequest = async (id) => {
    try {
      await leaveService.cancelLeaveRequest(id);
      toast({ title: "Leave request cancelled", status: "success" });
      loadRequests();
    } catch (error) {
      toast({
        title: "Cancel failed",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  return (
    <Box p={{ base: 4, md: 6 }}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg">Leave Requests</Heading>
          <Text mt={1} color="gray.500">
            Create new leave requests and track status.
          </Text>
        </Box>

        <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
          <ModalOverlay backdropFilter="blur(5px)" />
          <ModalContent borderRadius="lg" boxShadow="2xl">
            <ModalHeader
              bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              color="white"
              borderRadius="lg lg 0 0"
              pb={4}
            >
              <VStack align="start" spacing={1}>
                <Text fontSize="lg" fontWeight="700">
                  New Leave Request
                </Text>
                <Text fontSize="xs" opacity={0.9}>
                  Submit your leave application
                </Text>
              </VStack>
            </ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody pt={6} pb={4}>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel fontWeight="600" fontSize="sm" mb={2}>
                    Leave Policy
                  </FormLabel>
                  <Select
                    placeholder="Select a policy"
                    value={form.leavePolicyId}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        leavePolicyId: e.target.value,
                      }))
                    }
                    borderRadius="md"
                    borderColor="gray.300"
                    focusBorderColor="teal.500"
                  >
                    {policies.map((policy) => (
                      <option key={policy._id} value={policy._id}>
                        {policy.name} ({policy.code})
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <Grid templateColumns="repeat(2, 1fr)" gap={4} width="full">
                  <FormControl>
                    <FormLabel fontWeight="600" fontSize="sm" mb={2}>
                      From Date
                    </FormLabel>
                    <Input
                      type="date"
                      value={form.fromDate}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          fromDate: e.target.value,
                        }))
                      }
                      borderRadius="md"
                      borderColor="gray.300"
                      focusBorderColor="teal.500"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontWeight="600" fontSize="sm" mb={2}>
                      To Date
                    </FormLabel>
                    <Input
                      type="date"
                      value={form.toDate}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, toDate: e.target.value }))
                      }
                      borderRadius="md"
                      borderColor="gray.300"
                      focusBorderColor="teal.500"
                    />
                  </FormControl>
                </Grid>

                <FormControl>
                  <FormLabel fontWeight="600" fontSize="sm" mb={2}>
                    Reason for Leave
                  </FormLabel>
                  <Input
                    value={form.reason}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, reason: e.target.value }))
                    }
                    placeholder="Enter your reason"
                    borderRadius="md"
                    borderColor="gray.300"
                    focusBorderColor="teal.500"
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter borderTop="1px" borderColor="gray.200" pt={4}>
              <HStack spacing={3} width="full" justify="flex-end">
                <Button variant="outline" onClick={onClose} borderRadius="md">
                  Cancel
                </Button>
                <Button
                  colorScheme="teal"
                  onClick={submitRequest}
                  borderRadius="md"
                  fontWeight="600"
                >
                  Submit Request
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Box
          borderRadius="xl"
          p={3}
          bg="linear-gradient(135deg, #f0f7ff 0%, #f5f0ff 100%)"
          borderWidth="1px"
          borderColor="gray.200"
        >
          <VStack align="start" spacing={2}>
            <Box>
              <Heading size="xs" mb={0}>
                Leave Balance
              </Heading>
              <Text fontSize="2xs" color="gray.500">
                Remaining days by policy
              </Text>
            </Box>
            {policies.length === 0 ? (
              <Box
                width="full"
                borderRadius="lg"
                p={2}
                bg="white"
                textAlign="center"
                borderWidth="1px"
                borderColor="gray.200"
              >
                <Text color="gray.600" fontSize="2xs">
                  No policies. Contact HR.
                </Text>
              </Box>
            ) : (
              <SimpleGrid
                columns={{ base: 3, sm: 4, md: 5, lg: 8 }}
                spacing={2}
                width="full"
              >
                {policies.map((policy) => {
                  const balance = allBalances.find(
                    (b) => b?.leavePolicyId?._id === policy._id,
                  );

                  if (!balance) {
                    return (
                      <Box
                        key={policy._id}
                        borderRadius="lg"
                        p={2}
                        bg="white"
                        borderWidth="1px"
                        borderColor="gray.200"
                        textAlign="center"
                        opacity={0.55}
                        transition="all 0.2s ease"
                        _hover={{ boxShadow: "xs", opacity: 0.65 }}
                      >
                        <VStack spacing={1}>
                          <Text
                            fontWeight="600"
                            fontSize="2xs"
                            color="gray.700"
                            noOfLines={1}
                            h="16px"
                          >
                            {policy.code}
                          </Text>
                          <CircularProgress value={0} color="gray" size="50px">
                            <CircularProgressLabel
                              fontSize="2xs"
                              fontWeight="600"
                              color="gray.500"
                            >
                              ?
                            </CircularProgressLabel>
                          </CircularProgress>
                          <Text fontSize="2xs" color="gray.500">
                            -
                          </Text>
                        </VStack>
                      </Box>
                    );
                  }

                  const totalAllocated = balance.totalAllocated || 0;
                  const remaining = balance.remaining || 0;
                  const percentage =
                    totalAllocated > 0 ? (remaining / totalAllocated) * 100 : 0;

                  let progressColor = "green";
                  if (percentage <= 30) progressColor = "red";
                  else if (percentage <= 60) progressColor = "orange";

                  return (
                    <Box
                      key={balance._id}
                      borderRadius="lg"
                      p={2}
                      bg="white"
                      borderWidth="1px"
                      borderColor={`${progressColor}.200`}
                      textAlign="center"
                      boxShadow="xs"
                      transition="all 0.2s ease"
                      _hover={{
                        boxShadow: "sm",
                        transform: "scale(1.02)",
                      }}
                    >
                      <VStack spacing={1}>
                        <Text
                          fontWeight="700"
                          fontSize="2xs"
                          color="gray.800"
                          noOfLines={1}
                          h="16px"
                        >
                          {policy.code}
                        </Text>

                        <CircularProgress
                          value={percentage}
                          color={progressColor}
                          size="50px"
                          thickness="5px"
                        >
                          <CircularProgressLabel
                            fontSize="2xs"
                            fontWeight="700"
                            color={`${progressColor}.600`}
                          >
                            {remaining.toFixed(0)}
                          </CircularProgressLabel>
                        </CircularProgress>

                        <Text fontSize="2xs" color="gray.500" fontWeight="500">
                          {percentage.toFixed(0)}%
                        </Text>
                      </VStack>
                    </Box>
                  );
                })}
              </SimpleGrid>
            )}
          </VStack>
        </Box>

        <Box borderWidth="1px" borderRadius="md" p={4}>
          <Heading size="sm" mb={3}>
            Quick Balance Check
          </Heading>
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={3}
            align="flex-end"
          >
            <FormControl maxW={{ base: "full", md: "320px" }}>
              <FormLabel fontSize="sm" mb={2}>
                Select Policy
              </FormLabel>
              <Select
                placeholder="Select policy"
                value={selectedPolicyForBalance}
                onChange={(e) => {
                  setSelectedPolicyForBalance(e.target.value);
                  loadBalance(e.target.value);
                }}
                size="sm"
              >
                {policies.map((policy) => (
                  <option key={policy._id} value={policy._id}>
                    {policy.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            {balance && (
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={2}
                bg="gray.50"
                fontSize="sm"
              >
                <HStack spacing={4}>
                  <Box>
                    <Text color="gray.600" fontSize="xs">
                      Remaining
                    </Text>
                    <Text fontWeight="600">{balance.remaining ?? 0} days</Text>
                  </Box>
                  <Divider orientation="vertical" height="40px" />
                  <Box>
                    <Text color="gray.600" fontSize="xs">
                      Used
                    </Text>
                    <Text fontWeight="600">{balance.used ?? 0} days</Text>
                  </Box>
                </HStack>
              </Box>
            )}
          </Stack>
        </Box>

        <Box borderWidth="1px" borderRadius="md" p={4}>
          <Stack
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            spacing={3}
            mb={3}
          >
            <HStack spacing={2}>
              <Heading size="sm">My Leave Requests</Heading>
              <IconButton
                isRound
                size="sm"
                colorScheme="teal"
                icon={<AddIcon />}
                onClick={onOpen}
                aria-label="Create leave request"
              />
            </HStack>
            <Select
              value={filters.status}
              onChange={(e) => setFilters({ status: e.target.value })}
              maxW={{ base: "full", md: "220px" }}
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Cancelled">Cancelled</option>
            </Select>
          </Stack>

          <Box display={{ base: "none", md: "block" }}>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Policy</Th>
                  <Th>Code</Th>
                  <Th>Type</Th>
                  <Th>From</Th>
                  <Th>To</Th>
                  <Th>Days</Th>
                  <Th>Status</Th>
                  <Th>Reason</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {requests.map((request) => (
                  <Tr key={request._id}>
                    <Td>{request.leavePolicyId?.name || "-"}</Td>
                    <Td>
                      <Badge colorScheme="blue">
                        {request.leavePolicyId?.code || "-"}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          request.leavePolicyId?.type === "Paid"
                            ? "green"
                            : "gray"
                        }
                        variant="subtle"
                      >
                        {request.leavePolicyId?.type || "-"}
                      </Badge>
                    </Td>
                    <Td>{new Date(request.fromDate).toLocaleDateString()}</Td>
                    <Td>{new Date(request.toDate).toLocaleDateString()}</Td>
                    <Td>
                      {request.totalDays || 0}
                      {request.isHalfDay && (
                        <Text fontSize="xs" color="gray.500">
                          (Half)
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          request.status === "Approved"
                            ? "green"
                            : request.status === "Rejected"
                              ? "red"
                              : request.status === "Cancelled"
                                ? "gray"
                                : "orange"
                        }
                      >
                        {request.status}
                      </Badge>
                    </Td>
                    <Td>{request.reason}</Td>
                    <Td>
                      {request.status === "Pending" && (
                        <Button
                          size="xs"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => cancelRequest(request._id)}
                        >
                          Cancel
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          <Box display={{ base: "block", md: "none" }}>
            {requests.length === 0 ? (
              <Box borderWidth="1px" borderRadius="md" p={4} textAlign="center">
                <Text color="gray.500">No leave requests found</Text>
              </Box>
            ) : (
              <SimpleGrid columns={1} spacing={3}>
                {requests.map((request) => (
                  <Box
                    key={request._id}
                    borderWidth="1px"
                    borderRadius="md"
                    p={3}
                  >
                    <HStack justify="space-between" mb={2}>
                      <HStack spacing={2}>
                        <Text fontWeight="600">
                          {request.leavePolicyId?.name || "-"}
                        </Text>
                        <Badge colorScheme="blue" fontSize="xs">
                          {request.leavePolicyId?.code || "-"}
                        </Badge>
                      </HStack>
                      <Badge
                        colorScheme={
                          request.status === "Approved"
                            ? "green"
                            : request.status === "Rejected"
                              ? "red"
                              : request.status === "Cancelled"
                                ? "gray"
                                : "orange"
                        }
                      >
                        {request.status}
                      </Badge>
                    </HStack>
                    <HStack spacing={2} mb={2}>
                      <Badge
                        colorScheme={
                          request.leavePolicyId?.type === "Paid"
                            ? "green"
                            : "gray"
                        }
                        variant="subtle"
                        fontSize="xs"
                      >
                        {request.leavePolicyId?.type || "-"}
                      </Badge>
                      <Text fontSize="xs" fontWeight="500">
                        {request.totalDays || 0} day
                        {request.totalDays !== 1 ? "s" : ""}
                        {request.isHalfDay && " (Half Day)"}
                      </Text>
                    </HStack>
                    <Divider my={2} />
                    <Text fontSize="sm" color="gray.600">
                      {new Date(request.fromDate).toLocaleDateString()} -{" "}
                      {new Date(request.toDate).toLocaleDateString()}
                    </Text>
                    <Text fontSize="sm" mt={1} noOfLines={3}>
                      {request.reason}
                    </Text>
                    {request.status === "Pending" && (
                      <Button
                        mt={3}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => cancelRequest(request._id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </Box>
        </Box>
      </VStack>
    </Box>
  );
};

export default LeaveRequest;
